/* eslint-disable no-unused-vars */
import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { ChatCircleDots, PaperPlaneRight } from "@phosphor-icons/react";

const CHANNELS = [
  { key: "in_app",         label: "In-app" },
  { key: "email",          label: "Email" },
  { key: "whatsapp",       label: "WhatsApp" },
  { key: "phone",          label: "Phone" },
  { key: "field_visit",    label: "Field visit" },
  { key: "client_meeting", label: "Client meeting" },
];

export default function CaseUpdatesTab({ caseId, compact = false }) {
  const { user } = useAuth();
  const [updates, setUpdates] = useState([]);
  const [text, setText] = useState("");
  const [channel, setChannel] = useState("in_app");
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");

  const load = async () => {
    if (!caseId) return;
    setLoading(true);
    try { const { data } = await api.get(`/cases/${caseId}/updates`); setUpdates(data.updates || []); }
    catch (e) { setErr(formatApiError(e.response?.data?.detail) || "Load failed."); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [caseId]);

  const post = async () => {
    if (!text.trim()) return;
    setErr("");
    try {
      await api.post(`/cases/${caseId}/updates`, { text, channel });
      setText("");
      await load();
    } catch (e) { setErr(formatApiError(e.response?.data?.detail) || "Post failed."); }
  };

  // group by date (yyyy-mm-dd)
  const groups = {};
  for (const u of updates) {
    const day = (u.created_at || "").slice(0, 10);
    if (!groups[day]) groups[day] = [];
    groups[day].push(u);
  }
  const days = Object.keys(groups).sort().reverse();

  return (
    <div data-testid="case-updates-tab" className="space-y-3">
      <div className="bg-white border border-[var(--border)] rounded p-3 flex gap-2 items-start">
        <textarea data-testid="updates-text" value={text} onChange={e => setText(e.target.value)} rows={2}
          placeholder="Post an update… (visible to all assigned team members)"
          className="flex-1 border border-[var(--border)] rounded px-3 py-2 text-sm" />
        <div className="flex flex-col gap-2 w-32">
          <select data-testid="updates-channel" value={channel} onChange={e => setChannel(e.target.value)}
            className="border border-[var(--border)] rounded px-2 py-1 text-xs">
            {CHANNELS.map(c => <option key={c.key} value={c.key}>{c.label}</option>)}
          </select>
          <button data-testid="updates-post" onClick={post} disabled={!text.trim()}
            className="bg-[#F47D20] text-white text-xs px-2 py-1 rounded flex items-center gap-1 justify-center hover:bg-[#c96009] disabled:opacity-50">
            <PaperPlaneRight size={14} /> Post
          </button>
        </div>
      </div>
      {err && <div data-testid="updates-err" className="text-xs bg-rose-50 border border-rose-200 text-rose-700 px-3 py-2 rounded">{err}</div>}
      {loading ? <div className="text-xs text-[var(--text-faint)]">Loading…</div>
        : updates.length === 0 ? <div data-testid="updates-empty" className="text-xs text-[var(--text-faint)] px-3 py-6 text-center bg-white border border-[var(--border)] rounded">No updates yet. Post the first one.</div>
        : (
        <div className="space-y-3">
          {days.map(day => (
            <div key={day} data-testid={`updates-day-${day}`}>
              <div className="text-[11px] uppercase tracking-wider text-[var(--text-faint)] mb-1 px-1">{day}</div>
              <div className="bg-white border border-[var(--border)] rounded divide-y divide-[var(--border)]">
                {groups[day].map(u => (
                  <div key={u.update_id} data-testid={`updates-item-${u.update_id}`} className="px-3 py-2.5 flex gap-3 items-start">
                    <div className="w-8 h-8 rounded-full bg-[#263776] text-white text-xs font-semibold flex items-center justify-center shrink-0">{u.author_initials || "U"}</div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 text-xs mb-0.5">
                        <span className="font-semibold text-[var(--text-primary)]">{u.author_name}</span>
                        <span className="px-1.5 py-0.5 rounded text-[10px] bg-[var(--surface-3)] text-[var(--text-secondary)]">{u.channel}</span>
                        <span className="text-[var(--text-faint)]">{(u.created_at || "").slice(11, 16)}</span>
                        {u.edited && <span className="text-[10px] text-amber-600">(edited)</span>}
                      </div>
                      <div className="text-sm text-[var(--text-primary)] whitespace-pre-wrap">{u.text}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
